import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Bug, Lightbulb, RefreshCw, MessageSquarePlus, CheckCircle2, XCircle, Clock, Smartphone, Monitor } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "open",        label: "Åpen",        color: "bg-amber-100 text-amber-800 border-amber-200",     icon: Clock },
  { value: "in_progress", label: "Pågår",       color: "bg-blue-100 text-blue-800 border-blue-200",        icon: RefreshCw },
  { value: "resolved",    label: "Løyst",       color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  { value: "wontfix",     label: "Avvist",      color: "bg-stone-200 text-stone-700 border-stone-300",     icon: XCircle },
];

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  const Icon = opt.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${opt.color}`} data-testid={`feedback-status-${status}`}>
      <Icon className="h-3 w-3" /> {opt.label}
    </span>
  );
}

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const res = await api.listFeedback(params);
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikkje hente tilbakemeldingar");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (item, status) => {
    try {
      await api.updateFeedback(item.id, { status });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status } : i)));
      toast.success("Status oppdatert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved oppdatering");
    }
  };

  const counts = STATUS_OPTIONS.map((s) => ({ ...s, count: items.filter((i) => i.status === s.value).length }));

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="admin-feedback-page">
      <header className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <MessageSquarePlus className="h-6 w-6 text-navy-800" />
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Tilbakemeldingar</h1>
        </div>
        <p className="text-sm text-stone-500">Alle feilmeldingar og forbedringsforslag frå brukarane.</p>
      </header>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36 bg-white" data-testid="filter-status"><SelectValue placeholder="Alle status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle status</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 bg-white" data-testid="filter-category"><SelectValue placeholder="Alle kategori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle kategori</SelectItem>
              <SelectItem value="issue">Feil / bug</SelectItem>
              <SelectItem value="improvement">Forbedring</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={load}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:bg-stone-50"
            data-testid="reload-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Oppdater
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1.5 text-xs text-stone-500">
          {counts.map((c) => (
            <span key={c.value} className="inline-flex items-center gap-1">
              <StatusBadge status={c.value} /> {c.count}
            </span>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-sm text-stone-500 py-12">Lastar…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-sm text-stone-500 py-16 border border-dashed border-stone-200 rounded-lg">
          Ingen tilbakemeldingar enno.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="feedback-list">
          {items.map((item) => {
            const CatIcon = item.category === "improvement" ? Lightbulb : Bug;
            const catColor = item.category === "improvement" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-red-700 bg-red-50 border-red-200";
            const date = item.created_at ? new Date(item.created_at).toLocaleString("nb-NO") : "";
            const meta = item.meta || {};
            const isPWA = meta.pwa;
            const isMobile = meta.viewport && parseInt(meta.viewport.split("x")[0], 10) < 768;
            return (
              <li
                key={item.id}
                className="border border-stone-200 rounded-lg bg-white p-4 hover:shadow-sm transition-shadow"
                data-testid={`feedback-item-${item.id}`}
              >
                <div className="flex items-start gap-3">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${catColor} shrink-0`}>
                    <CatIcon className="h-3 w-3" /> {item.category === "improvement" ? "Forbedring" : "Feil"}
                  </span>
                  <div className="flex-1 min-w-0">
                    {item.title && (
                      <div className="font-semibold text-sm text-stone-900 mb-0.5">{item.title}</div>
                    )}
                    <div className="text-sm text-stone-700 whitespace-pre-wrap mb-2">{item.message}</div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-stone-500">
                      <span className="font-medium text-stone-700">{item.user_name || item.user_email || "Anonym"}</span>
                      {item.page && <span>📍 {item.page}</span>}
                      <span>🕒 {date}</span>
                      {meta.viewport && (
                        <span className="inline-flex items-center gap-0.5">
                          {isMobile ? <Smartphone className="h-3 w-3" /> : <Monitor className="h-3 w-3" />}
                          {meta.viewport}
                        </span>
                      )}
                      {isPWA && <span className="inline-flex items-center gap-0.5 px-1 rounded bg-purple-100 text-purple-800">PWA</span>}
                      {meta.ua && (
                        <span className="truncate max-w-xs text-stone-400 cursor-help" title={meta.ua}>
                          {meta.ua.split(" ").slice(0, 3).join(" ")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0">
                    <Select value={item.status || "open"} onValueChange={(v) => setStatus(item, v)}>
                      <SelectTrigger className="w-32 h-7 text-xs" data-testid={`status-select-${item.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
